import { useCallback, useEffect, useRef, useState } from "react";
import InputSearch from "../../../components/InputSearch";
import TabelaAdmin from "../../../components/TabelaAdmin";
import Esqueleto, { Recarregando } from "../../../components/Esqueleto";
import Paginacao from "../../../components/Paginacao";
import { mensagemDeErro, respostaOk } from "../../../../utils/resposta";

/**
 * Lista administrativa paginada (páginas numeradas), com busca, para os cadastros
 * de conteúdo (biomas, espécies, banners, apoiadores).
 *
 * `listar(page, limit, busca)` devolve a resposta do axios com { count, rows }.
 * Cada página SUBSTITUI a anterior (não acrescenta): trocar de página, de busca
 * ou de tamanho refaz a consulta. `versao` sobe quando quem usa alterou algo por
 * fora: refaz a página atual (e, se ela sumiu por uma exclusão, volta para a
 * última que existe).
 */
export default function ListaPaginada({
  rotulo, listar, colunas, chave, versao = 0, porPagina: porPaginaInicial = 20, itens = "itens",
  textoBusca, placeholderBusca, rotuloNovo, onNovo, vazio, larguraMinima = 800,
}) {
  const [page, setPage] = useState(1);
  const [porPagina, setPorPagina] = useState(porPaginaInicial);
  const [busca, setBusca] = useState("");
  const topo = useRef(null);
  const [dados, setDados] = useState({ count: 0, rows: [] });
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState("");
  const [typing, setTyping] = useState();
  const requisicao = useRef(0);
  // `chave` costuma ser uma função nova a cada render: guardada em ref para não
  // refazer a consulta toda vez.
  const chaveRef = useRef(chave);
  chaveRef.current = chave;

  const buscar = useCallback(async () => {
    const atual = ++requisicao.current;
    setLoading(true);
    const resultado = await listar(page, porPagina, busca);
    if (atual !== requisicao.current) return; // resposta de uma consulta velha

    if (respostaOk(resultado)) {
      const { count, rows } = resultado.data;
      // A página atual deixou de existir (excluiu o último item dela): vai para a
      // última que existe, em vez de mostrar "nada" com a lista ainda cheia.
      if (rows.length === 0 && count > 0 && page > 1) {
        setPage(Math.ceil(count / porPagina));
        return;
      }
      setErro("");
      setDados(resultado.data);
    } else {
      setErro(mensagemDeErro(resultado));
    }
    setLoading(false);
  }, [listar, page, porPagina, busca]);

  useEffect(() => { buscar(); }, [buscar]);

  const primeira = useRef(true);
  useEffect(() => {
    if (primeira.current) { primeira.current = false; return; }
    buscar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);

  function irParaPagina(n) {
    setPage(n);
    // A lista é longa: ao trocar de página volta ao começo dela.
    topo.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  function aoDigitar(e) {
    const valor = e.target.value;
    clearTimeout(typing);
    setTyping(setTimeout(() => { setBusca(valor); setPage(1); }, 500));
  }

  return (
    <>
      <div className="barra" ref={topo}>
        <div className="busca">
          <InputSearch text={textoBusca} placeholder={placeholderBusca} onChangeCapture={aoDigitar} />
        </div>
        {onNovo && <button type="button" className="novo" onClick={onNovo}>{rotuloNovo}</button>}
      </div>

      {erro && <span className="erro-lista" role="alert">{erro}</span>}

      {!loading && !erro && dados.rows.length === 0 &&
        <span className="vazio">{busca ? "Nada encontrado para essa busca." : vazio}</span>
      }

      {/* Sem overlay: esqueleto na primeira carga; em busca/pagina a tabela antiga fica mais clara. */}
      {loading && dados.rows.length === 0 && <Esqueleto variante="linha" quantidade={6} rotulo={`Carregando ${itens || "lista"}`} />}
      <Recarregando $ativo={loading && dados.rows.length > 0} aria-busy={loading && dados.rows.length > 0}>
        <TabelaAdmin rotulo={rotulo} colunas={colunas} linhas={dados.rows} chave={chave} larguraMinima={larguraMinima} />
      </Recarregando>

      {!(loading && dados.rows.length === 0) &&
      <Paginacao
        pagina={page} total={dados.count} porPagina={porPagina} itens={itens}
        onMudarPagina={irParaPagina}
        onMudarPorPagina={(n) => { setPorPagina(n); setPage(1); }}
      />
      }
    </>
  );
}
